import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { AlertTriangle, X } from 'lucide-react';

interface ConfirmationOptions {
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'warning' | 'info';
}

interface ConfirmationContextType {
  confirm: (options: ConfirmationOptions) => Promise<boolean>;
}

const ConfirmationContext = createContext<ConfirmationContextType | null>(null);

interface ModalState {
  isOpen: boolean;
  options: ConfirmationOptions | null;
}

const variantStyles = {
  warning: {
    iconBg: 'bg-amber-100',
    icon: 'text-amber-500',
    button: 'bg-amber-500 hover:bg-amber-600 text-white',
  },
  info: {
    iconBg: 'bg-blue-100',
    icon: 'text-blue-500',
    button: 'bg-blue-500 hover:bg-blue-600 text-white',
  },
};

const ConfirmationModal: React.FC<{
  state: ModalState;
  onConfirm: () => void;
  onCancel: () => void;
}> = ({ state, onConfirm, onCancel }) => {
  if (!state.isOpen || !state.options) return null;

  const { options } = state;
  const variant = options.variant ?? 'warning';
  const styles = variantStyles[variant];

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && onCancel()}
    >
      <div className="absolute inset-0 bg-white/30 backdrop-blur-md" />

      <div className="relative bg-white/85 backdrop-blur-xl border border-white/50 shadow-2xl rounded-3xl w-full max-w-md overflow-hidden animate-in zoom-in-95 fade-in duration-200">
        <div className="flex items-center justify-between p-6 pb-0">
          <div className="flex items-center gap-3">
            <div className={`p-2 ${styles.iconBg} rounded-xl`}>
              <AlertTriangle className={styles.icon} size={24} />
            </div>
            <h3 className="text-xl font-bold text-slate-800">{options.title}</h3>
          </div>
          <button
            onClick={onCancel}
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-xl transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-6">
          <p className="text-sm text-slate-600">{options.message}</p>
        </div>

        <div className="flex gap-3 p-6 pt-0">
          <button
            onClick={onCancel}
            className="flex-1 px-4 py-3 text-slate-600 font-medium bg-slate-100 hover:bg-slate-200 rounded-xl transition-colors"
          >
            {options.cancelText ?? 'Cancelar'}
          </button>
          <button
            onClick={onConfirm}
            className={`flex-1 px-4 py-3 font-bold rounded-xl transition-colors ${styles.button}`}
          >
            {options.confirmText ?? 'Confirmar'}
          </button>
        </div>
      </div>
    </div>
  );
};

export const ConfirmationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [modalState, setModalState] = useState<ModalState>({ isOpen: false, options: null });
  const resolveRef = useRef<((value: boolean) => void) | null>(null);

  const confirm = useCallback((options: ConfirmationOptions): Promise<boolean> => {
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setModalState({ isOpen: true, options });
    });
  }, []);

  const handleConfirm = useCallback(() => {
    setModalState({ isOpen: false, options: null });
    resolveRef.current?.(true);
    resolveRef.current = null;
  }, []);

  const handleCancel = useCallback(() => {
    setModalState({ isOpen: false, options: null });
    resolveRef.current?.(false);
    resolveRef.current = null;
  }, []);

  return (
    <ConfirmationContext.Provider value={{ confirm }}>
      {children}
      <ConfirmationModal state={modalState} onConfirm={handleConfirm} onCancel={handleCancel} />
    </ConfirmationContext.Provider>
  );
};

export const useConfirmation = (): ConfirmationContextType => {
  const context = useContext(ConfirmationContext);
  if (!context) throw new Error('useConfirmation must be used within a ConfirmationProvider');
  return context;
};
